import { AnimatePresence, motion } from 'motion/react';

import { useVacancyFilterCombobox } from './useVacancyFilterCombobox';
import { VacancyFilterComboboxOption } from './VacancyFilterComboboxOption';

import { Chevron } from '~/components/ui/icons';
import { cn } from '~/lib/utils';

type VacancyFilterComboboxText = string | { value?: string };

const getVacancyFilterText = (value: VacancyFilterComboboxText) => {
  if (typeof value === 'string') {
    return value;
  }

  return value.value ?? '';
};

const getFilterTriggerLabel = (
  selectedValues: string[],
  fallbackLabel: string
) => {
  if (selectedValues.length === 0) {
    return fallbackLabel;
  }

  return selectedValues.join(', ');
};

interface VacancyFilterComboboxProps {
  label: VacancyFilterComboboxText;
  options: string[];
  selectedValues: string[];
  onToggle: (value: string) => void;
}

export const VacancyFilterCombobox = ({
  label,
  options,
  selectedValues,
  onToggle,
}: VacancyFilterComboboxProps) => {
  const labelText = getVacancyFilterText(label);
  const triggerLabel = getFilterTriggerLabel(selectedValues, labelText);
  const isDisabled = options.length === 0;
  const {
    handleOptionKeyDown,
    handleTriggerKeyDown,
    isOpen,
    listboxId,
    optionRefs,
    rootRef,
    setIsOpen,
    triggerRef,
  } = useVacancyFilterCombobox({
    isDisabled,
    onToggle,
    options,
    selectedValues,
  });

  return (
    <div ref={rootRef} className="relative w-full min-w-0">
      <button
        ref={triggerRef}
        type="button"
        role="combobox"
        aria-controls={listboxId}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-label={labelText}
        disabled={isDisabled}
        className={cn(
          'flex h-[46px] w-full min-w-0 cursor-pointer items-center justify-between gap-4 border border-graphite-dark bg-transparent px-3 py-2.5 text-left text-graphite-700 transition-colors hover:text-white focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-2 focus-visible:outline-blue-primary disabled:cursor-not-allowed disabled:opacity-50',
          (isOpen || selectedValues.length > 0) && 'text-white'
        )}
        onClick={() => setIsOpen((currentValue) => !currentValue)}
        onKeyDown={handleTriggerKeyDown}
      >
        <span className="min-w-0 flex-1 truncate font-manrope text-[16px] leading-6 tracking-[0.16px]">
          {triggerLabel}
        </span>
        <Chevron
          className={cn(
            'ml-auto size-[14px] shrink-0 text-current transition-transform duration-200',
            isOpen && 'rotate-180'
          )}
        />
      </button>

      <AnimatePresence>
        {isOpen ? (
          <motion.div
            initial={{ clipPath: 'inset(0 0 100% 0)' }}
            animate={{ clipPath: 'inset(0 0 0% 0)' }}
            exit={{ clipPath: 'inset(0 0 100% 0)' }}
            transition={{ duration: 0.25, ease: 'easeInOut' }}
            className="absolute left-0 top-full z-20 mt-2 w-full overflow-hidden bg-graphite-dark"
          >
            <div
              id={listboxId}
              role="listbox"
              aria-multiselectable="true"
              className="max-h-[320px] overflow-auto"
            >
              {options.map((option, index) => {
                const isSelected = selectedValues.includes(option);

                return (
                  <VacancyFilterComboboxOption
                    key={option}
                    option={option}
                    isSelected={isSelected}
                    inputRef={(node) => {
                      optionRefs.current[index] = node;
                    }}
                    onToggle={() => onToggle(option)}
                    onKeyDown={(event) =>
                      handleOptionKeyDown(event, option, index)
                    }
                  />
                );
              })}
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
};
